/**
 * Body component for the Indigo design system.
 *
 * A polymorphic text component for body content with multiple size variants.
 * Supports rendering as different HTML elements while maintaining consistent typography styles.
 *
 * Features:
 * - Five size variants: lg, md (default), sm, xs, xxs
 * - Polymorphic component - can render as different elements via 'as' prop
 * - Design system typography styles
 * - Support for Tailwind utility classes via className prop
 * - TypeScript-first with proper prop types
 * - Forwards ref for DOM manipulation
 *
 * @example
 * ```tsx
 * // Default: renders as <p> with medium size
 * <Body>This is body text</Body>
 *
 * // Large body text
 * <Body size="lg">Large body text</Body>
 *
 * // Render as span instead of p
 * <Body as="span" size="sm">Inline body text</Body>
 *
 * // With additional Tailwind utilities
 * <Body size="xs" className="text-indigo-text-secondary">
 *   Secondary text
 * </Body>
 * ```
 */
import { cva } from "class-variance-authority";
import { ComponentPropsWithRef, ElementType, forwardRef } from "react";
import "./Typography.css";

const bodyVariants = cva("indigo-typography-base indigo-typography-body", {
  variants: {
    size: {
      lg: "indigo-typography-body-lg",
      md: "indigo-typography-body-md",
      sm: "indigo-typography-body-sm",
      xs: "indigo-typography-body-xs",
      xxs: "indigo-typography-body-xxs",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

type BodyOwnProps<E extends ElementType = ElementType> = {
  /**
   * The HTML element to render as
   * @default "p"
   */
  as?: E;

  /**
   * The size variant of the body text
   * @default "md"
   */
  size?: "lg" | "md" | "sm" | "xs" | "xxs";

  /**
   * Additional CSS classes to apply
   */
  className?: string;

  /**
   * The content to display
   */
  children?: React.ReactNode;
};

export type BodyProps<E extends ElementType = "p"> = BodyOwnProps<E> &
  Omit<ComponentPropsWithRef<E>, keyof BodyOwnProps>;

/**
 * Body component
 *
 * Renders a polymorphic text element with consistent body typography styles.
 *
 * @param props - Component props (@see BodyProps)
 * @returns A React text component (p, span, div, etc.)
 */
export const Body = forwardRef(
  <E extends ElementType = "p">(
    { as, size = "md", className, children, ...props }: BodyProps<E>,
    ref: React.Ref<any>,
  ) => {
    const Component = as || "p";
    const bodyClass = bodyVariants({ size });
    const combinedClassName = className
      ? `${bodyClass} ${className}`
      : bodyClass;

    return (
      <Component ref={ref} className={combinedClassName} {...props}>
        {children}
      </Component>
    );
  },
);

Body.displayName = "Body";

export { bodyVariants };
